import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../../../context/AuthContext/AuthContext';
import { useFundedCta } from '../../../../../features/application/hooks/useFundedCta';

import { styled, Box, Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';

import FullLogo from '../../../../../assets/images/fullLogo.png';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const DrawerHeaderWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface HeaderProps {
  open: boolean;
  handleDrawerClose: () => void;
  logoSrc: string;
  isMobile: boolean;
  isRtl?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  open,
  handleDrawerClose,
  isMobile,
  isRtl = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['sidebar']);
  const fundedCta = useFundedCta();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ROLE_ADMIN';

  return (
    <DrawerHeaderWrapper
      sx={{
        marginBottom: open ? '-10px' : '-16px',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <Stack sx={{ width: '100%', alignItems: 'center' }}>
        <Stack
          alignItems={isMobile ? 'start' : 'end'}
          mt={6}
          sx={{
            ...(!open && { display: 'none' }),
            width: '100%',
          }}
        >
          <IconButton
            aria-label="close drawer"
            onClick={handleDrawerClose}
            edge="start"
            sx={{ alignSelf: isMobile ? 'flex-start' : isRtl ? 'flex-start' : 'flex-end' }}
          >
            <Tooltip title={t('HEADER.BUTTON_REDUCE')}>
              {isRtl ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </Tooltip>
          </IconButton>
        </Stack>

        <Stack
          justifyContent="center"
          alignItems="center"
          spacing={2}
          mb={8}
          mt={isMobile ? 4 : 0}
          sx={{ width: '100%' }}
        >
          <Box
            component={Link}
            to={'/'}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Box
              component="img"
              sx={{
                height: 'auto',
                width: '100%',
                maxWidth: 180,
                display: 'block',
                transform: 'translateX(-10px)',
                ...(!open && { display: 'none' }),
              }}
              alt="RaiseMyFunds logo"
              src={FullLogo}
            />
          </Box>

          {isAdmin ? (
            <Chip
              label={t('HEADER.ADMINISTRATION')}
              sx={{
                ...(!open && { display: 'none' }),
                backgroundColor: '#2B2D3A',
                color: '#FFFFFF',
                fontWeight: 400,
                fontSize: '0.8rem',
                letterSpacing: '0.06em',
                border: 'none',
                borderRadius: '8px',
                width: '100%',
              }}
            />
          ) : (
            <Button
              size="small"
              variant="contained"
              fullWidth
              onClick={() => {
                navigate(fundedCta.to);
                isMobile && handleDrawerClose();
              }}
              sx={{
                ...(!open && { display: 'none' }),
                fontWeight: 'bold',
              }}
            >
              {fundedCta.label}
            </Button>
          )}
        </Stack>
      </Stack>
    </DrawerHeaderWrapper>
  );
};

export default Header;
